/**
 * InlineEffectsPanel Component
 * Extracted from DAWWorkspace.jsx for better modularity
 * Handles track effects (EQ, reverb, delay) and metronome/count-in settings
 */

import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { X, Circle, Headphones, Sparkles, ChevronRight } from 'lucide-react';

// Simple metronome icon component
const MetronomeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v10M8 22h8l-2-18H10l-2 18z" />
    <path d="M12 12l6-6" />
  </svg>
);

export const InlineEffectsPanel = ({ track, onUpdate, onClose, metronome, countIn, onOpenAdvancedEffects, isPro }) => {
  const updateEQ = (band, value) => {
    if (!track) return;
    onUpdate({
      eq: { ...track.eq, [band]: value }
    });
  };

  const updateEffect = (effect, value) => {
    if (!track) return;
    onUpdate({
      effects: { ...track.effects, [effect]: value }
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
        <span className="font-semibold text-sm">Effects & Settings</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="p-1 text-zinc-400 hover:text-white"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Count-In Section */}
        <div>
          <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
            <Circle className="w-3 h-3" />
            Count-In (Pre-Roll)
          </h3>
          
          <div className="space-y-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Before Recording</span>
              <Button
                variant={countIn.enabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => countIn.setEnabled(!countIn.enabled)}
                className={`h-7 px-3 text-xs ${countIn.enabled ? 'bg-red-500 hover:bg-red-600' : 'border-zinc-700'}`}
                data-testid="countin-enable"
              >
                {countIn.enabled ? 'ON' : 'OFF'}
              </Button>
            </div>
            
            {/* Number of beats */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Count-In Beats</span>
                <span className="font-mono text-red-400">{countIn.beats} beats</span>
              </div>
              <div className="flex gap-2">
                {[2, 4, 8].map(beats => (
                  <Button
                    key={beats}
                    variant={countIn.beats === beats ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => countIn.setBeats(beats)}
                    className={`flex-1 h-8 text-xs ${countIn.beats === beats ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'border-zinc-700'}`}
                    data-testid={`countin-${beats}`}
                  >
                    {beats}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-zinc-600">Metronome clicks before recording starts</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-zinc-800" />
        
        {/* Metronome Section */}
        <div>
          <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
            <MetronomeIcon className="w-3 h-3" />
            Metronome
          </h3>
          
          <div className="space-y-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">Click Track</span>
              <Button
                variant={metronome.enabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => metronome.setEnabled(!metronome.enabled)}
                className={`h-7 px-3 text-xs ${metronome.enabled ? 'bg-indigo-500 hover:bg-indigo-600' : 'border-zinc-700'}`}
                data-testid="metronome-enable"
              >
                {metronome.enabled ? 'ON' : 'OFF'}
              </Button>
            </div>
            
            {/* Volume */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Volume</span>
                <span className="font-mono text-indigo-400">{Math.round(metronome.volume * 100)}%</span>
              </div>
              <Slider
                value={[metronome.volume * 100]}
                onValueChange={([v]) => metronome.setVolume(v / 100)}
                max={100}
                step={1}
                className="w-full"
                data-testid="metronome-volume"
              />
            </div>
            
            {/* Time Signature */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Time Signature</span>
                <span className="font-mono text-indigo-400">{metronome.timeSignature}/4</span>
              </div>
              <div className="flex gap-2">
                {[3, 4, 5, 6].map(ts => (
                  <Button
                    key={ts}
                    variant={metronome.timeSignature === ts ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => metronome.setTimeSignature(ts)}
                    className={`flex-1 h-8 text-xs ${metronome.timeSignature === ts ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300' : 'border-zinc-700'}`}
                  >
                    {ts}/4
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Beat Indicator */}
            {metronome.enabled && (
              <div className="flex justify-center gap-2 py-2">
                {Array.from({ length: metronome.timeSignature }, (_, i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full transition-all ${
                      metronome.currentBeat === i + 1
                        ? i === 0
                          ? 'bg-indigo-500 scale-125 shadow-lg shadow-indigo-500/50'
                          : 'bg-cyan-500 scale-110 shadow-lg shadow-cyan-500/50'
                        : 'bg-zinc-700'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-zinc-800" />

        {/* Track EQ Section */}
        {track ? (
          <>
            <div>
              <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${track.color}`} />
                {track.name} - Equalizer
              </h3>
              
              <div className="space-y-4">
                {/* Bass */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Bass</span>
                    <span className="font-mono text-indigo-400">{track.eq.bass > 0 ? '+' : ''}{Math.round(track.eq.bass * 12)}dB</span>
                  </div>
                  <Slider
                    value={[(track.eq.bass + 1) * 50]}
                    onValueChange={([v]) => updateEQ('bass', (v / 50) - 1)}
                    max={100}
                    step={1}
                    className="w-full"
                    data-testid="eq-bass"
                  />
                </div>

                {/* Mid */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Mid</span>
                    <span className="font-mono text-indigo-400">{track.eq.mid > 0 ? '+' : ''}{Math.round(track.eq.mid * 12)}dB</span>
                  </div>
                  <Slider
                    value={[(track.eq.mid + 1) * 50]}
                    onValueChange={([v]) => updateEQ('mid', (v / 50) - 1)}
                    max={100}
                    step={1}
                    className="w-full"
                    data-testid="eq-mid"
                  />
                </div>

                {/* Treble */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Treble</span>
                    <span className="font-mono text-indigo-400">{track.eq.treble > 0 ? '+' : ''}{Math.round(track.eq.treble * 12)}dB</span>
                  </div>
                  <Slider
                    value={[(track.eq.treble + 1) * 50]}
                    onValueChange={([v]) => updateEQ('treble', (v / 50) - 1)}
                    max={100}
                    step={1}
                    className="w-full"
                    data-testid="eq-treble"
                  />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-zinc-800" />

            {/* Effects Section */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 flex items-center gap-2">
                <Headphones className="w-3 h-3" />
                Effects
              </h3>
              
              <div className="space-y-4">
                {/* Reverb */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Reverb</span>
                    <span className="font-mono text-cyan-400">{Math.round(track.effects.reverb * 100)}%</span>
                  </div>
                  <Slider
                    value={[track.effects.reverb * 100]}
                    onValueChange={([v]) => updateEffect('reverb', v / 100)}
                    max={100}
                    step={1}
                    className="w-full"
                    data-testid="fx-reverb"
                  />
                </div>

                {/* Delay */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Delay</span>
                    <span className="font-mono text-amber-400">{Math.round(track.effects.delay * 100)}%</span>
                  </div>
                  <Slider
                    value={[track.effects.delay * 100]}
                    onValueChange={([v]) => updateEffect('delay', v / 100)}
                    max={100}
                    step={1}
                    className="w-full"
                    data-testid="fx-delay"
                  />
                </div>
              </div>
            </div>

            {/* Advanced Effects Button */}
            <div className="pt-4 border-t border-zinc-800">
              <Button
                variant="outline"
                size="sm"
                onClick={onOpenAdvancedEffects}
                className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10 flex items-center justify-between group"
                data-testid="open-advanced-effects"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Advanced Effects
                </span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-xs text-zinc-500 mt-2 text-center">
                Reverb, Delay, Compression, EQ, Chorus, Flanger, Phaser, Distortion
              </p>
            </div>

            {/* Reset Button */}
            <div className="pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onUpdate({
                    eq: { bass: 0, mid: 0, treble: 0 },
                    effects: { reverb: 0, delay: 0 }
                  });
                }}
                className="w-full border-zinc-700 text-zinc-400 hover:text-white"
                data-testid="reset-effects"
              >
                Reset Track Effects
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center text-zinc-500 text-sm py-4">
            Select a track to edit EQ and effects
          </div>
        )}
      </div>
    </div>
  );
};

export default InlineEffectsPanel;
